"use client";

import { ChevronLeft } from "lucide-react";
import { useRouter } from "next/navigation";
import {
  type ComponentProps,
  type ReactNode,
  Suspense,
  useEffect,
  useRef,
} from "react";
import { Button } from "@/components/ui/button";
import Link from "@/components/ui/link";
import { useResolvedBackHref } from "@/hooks/use-current-href";
import { useSwipeBackTarget } from "@/hooks/use-swipe-back-target";
import { goBackTo } from "@/lib/go-back";
import { cn } from "@/lib/utils";

type BackButtonProps = {
  fallbackHref: string;
  label?: string;
  className?: string;
  iconClassName?: string;
  children?: ReactNode;
  showLabel?: boolean;
  variant?: ComponentProps<typeof Button>["variant"];
  size?: ComponentProps<typeof Button>["size"];
};

/**
 * The chevron fills most of an icon-only button: the glyph itself spans only
 * about half its box, so at the old 20px (which Button's
 * `[&_svg:not([class*='size-'])]:size-4` silently shrank to 16px) it read as
 * a speck in a 44px circle. A `size-*` class is required — `h-*`/`w-*` lose
 * to that Button rule.
 */
const BACK_ICON_STROKE = 2.5;

function backIconSize(hasText: boolean) {
  return hasText ? "size-4" : "size-7";
}

export function BackButton(props: BackButtonProps) {
  const hasText = props.showLabel || props.children !== undefined;

  return (
    <Suspense
      fallback={
        <Button
          asChild
          variant={props.variant ?? "ghost"}
          size={props.size ?? (hasText ? "sm" : "icon")}
          className={cn(
            !hasText && "-ml-2 h-9 w-9 rounded-full",
            props.className,
          )}
        >
          <Link href={props.fallbackHref} aria-label={props.label ?? "Back"}>
            <ChevronLeft
              className={cn(backIconSize(hasText), props.iconClassName)}
              strokeWidth={hasText ? 2 : BACK_ICON_STROKE}
              aria-hidden="true"
            />
            {props.children ??
              (props.showLabel ? (props.label ?? "Back") : null)}
          </Link>
        </Button>
      }
    >
      <BackButtonInner {...props} />
    </Suspense>
  );
}

function BackButtonInner({
  fallbackHref,
  label = "Back",
  className,
  iconClassName,
  children,
  showLabel = false,
  variant = "ghost",
  size,
}: BackButtonProps) {
  const router = useRouter();
  const href = useResolvedBackHref(fallbackHref);
  const hasText = showLabel || children !== undefined;
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const onNativeBack = (event: Event) => {
      if (event.defaultPrevented || !buttonRef.current?.getClientRects().length)
        return;
      event.preventDefault();
      goBackTo(router, href);
    };
    window.addEventListener("hiive:native-back", onNativeBack);
    return () => window.removeEventListener("hiive:native-back", onNativeBack);
  }, [router, href]);
  // Every screen with this arrow also gets the edge swipe, committing through
  // the exact handler a tap runs (see `installSwipeBack`).
  useSwipeBackTarget(buttonRef, () => goBackTo(router, href), {
    previewHref: href,
  });

  return (
    <Button
      ref={buttonRef}
      variant={variant}
      size={size ?? (hasText ? "sm" : "icon")}
      className={cn(!hasText && "-ml-2 h-9 w-9 rounded-full", className)}
      aria-label={label}
      // Popping lets Next 16 restore the preserved layout and exact scroll
      // instead of creating a fresh list navigation at scrollY=0 — but only
      // when the entry behind us is the one this arrow points at. See
      // `goBackTo`.
      onClick={() => goBackTo(router, href)}
    >
      <ChevronLeft
        className={cn(backIconSize(hasText), iconClassName)}
        strokeWidth={hasText ? 2 : BACK_ICON_STROKE}
        aria-hidden="true"
      />
      {children ?? (showLabel ? label : null)}
    </Button>
  );
}
